import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { isEmpty, maxBy, round, uniq } from 'lodash'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Button, Card, CardContent, Grid, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import { Close } from '@mui/icons-material'
import { CardTitle } from 'components/card/NewCard'
import Input from 'components/forms/Input'
import NumberField from 'components/forms/NumberField'
import Checkbox from 'components/forms/Checkbox'
import { darkBlue } from 'utils/constants/ColorTheme'
import { getDate, getDateWithHour, getFullDateMini, getHour } from 'utils/DateUtil'
import { formatHourInput, onChangeDate, onChangeHour, safeBaseDate } from 'utils/FormUtils'
import { hasValue } from 'utils/NumberUtil'
import { getHardPiezoDataTypes } from 'utils/PiezometryUtils'
import { getHardHydroDataTypes } from 'utils/HydroUtils'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import PiezometryAction from 'piezometry/actions/PiezometryAction'
import HydrometryAction from 'hydrometry/actions/HydrometryAction'
import PluviometryAction from 'pluviometry/actions/PluviometryAction'

// Type de donnée du niveau contrôlé par module (repris dans le champ ns de l'événement)
const LEVEL_DATA_TYPE = {
    [STATION_TYPE_NAME.piezometry]: -1,
    [STATION_TYPE_NAME.hydrometry]: 4,
}

const formatLastMeasure = m => ({
    dataType: m.dataType,
    value: m.value,
    date: m.date ?? m.measureDate ?? (m.initialCreationDate ? getDateWithHour(m.initialCreationDate, m.initialHour).valueOf() : undefined),
})

// Dernière mesure de chaque type de donnée présent sur la station
const fetchStationLastMeasures = (stationType, stationId) => {
    switch (stationType) {
        case STATION_TYPE_NAME.piezometry:
            return PiezometryAction.promisePiezoSituationLastMeasures([stationId]).then(json => (json || []).map(formatLastMeasure))
        case STATION_TYPE_NAME.pluviometry:
            return PluviometryAction.promisePluvioSituationLastMeasures([stationId]).then(json => (json || []).map(formatLastMeasure))
        case STATION_TYPE_NAME.hydrometry:
            // la situation hydro ne renvoie que hauteur, débit et types avec seuils : on part des statistiques de la station
            return HydrometryAction.promiseHydroStatistics(stationId).then(stats => Promise.all(
                uniq((stats || []).map(s => s.typeId)).filter(typeId => hasValue(typeId)).map(typeId =>
                    HydrometryAction.promiseHydroLastMeasures([stationId], typeId)
                        .then(json => {
                            const last = maxBy(json || [], 'date')
                            return { dataType: typeId, value: last?.value, date: last?.date }
                        })
                        .catch(() => ({ dataType: typeId })),
                ),
            ))
        default:
            return Promise.resolve([])
    }
}

// Largeurs des colonnes (sur 12)
const COLUMNS = { label: 2, date: 1.75, hour: 1.5, value: 1.5, lastValue: 2.25, difference: 1.25, recalibration: 1.75 }

const getControlDifference = control => (hasValue(control.value) && hasValue(control.lastValue) ? round(control.value - control.lastValue, 3) : undefined)

// Contrôles à enregistrer : seuls ceux avec une valeur sont conservés
const getControlsToSave = (controls = []) => controls.filter(c => hasValue(c.value))

// Chaque ligne affichée doit avoir une date, une heure et une valeur
const hasInvalidControls = (controls = []) => controls.some(c => !hasValue(c.value) || !hasValue(c.controlDate))

// Contrôle du niveau (ns de l'événement)
const getLevelControl = (stationType, controls = []) => getControlsToSave(controls).find(c => c.typeId === LEVEL_DATA_TYPE[stationType])

const useModuleDataTypes = (stationType) => {
    const dispatch = useDispatch()
    const {
        piezometryDataTypes,
        hydrometryDataTypes,
        pluviometryDataTypes,
    } = useSelector(store => ({
        piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
        pluviometryDataTypes: store.PluviometryReducer.pluviometryDataTypes,
    }), shallowEqual)

    useEffect(() => {
        if (stationType === STATION_TYPE_NAME.piezometry && isEmpty(piezometryDataTypes)) {
            dispatch(PiezometryAction.fetchPiezometryDataTypes())
        } else if (stationType === STATION_TYPE_NAME.hydrometry && isEmpty(hydrometryDataTypes)) {
            dispatch(HydrometryAction.fetchHydrometryDataTypes())
        } else if (stationType === STATION_TYPE_NAME.pluviometry && isEmpty(pluviometryDataTypes)) {
            dispatch(PluviometryAction.fetchPluviometryDataTypes())
        }
    }, [stationType])

    return useMemo(() => {
        switch (stationType) {
            case STATION_TYPE_NAME.piezometry:
                return [...getHardPiezoDataTypes().filter(dt => !piezometryDataTypes.some(odt => odt.id === dt.id)), ...piezometryDataTypes]
            case STATION_TYPE_NAME.hydrometry:
                return [...getHardHydroDataTypes().filter(dt => !hydrometryDataTypes.some(odt => odt.id === dt.id)), ...hydrometryDataTypes]
            case STATION_TYPE_NAME.pluviometry:
                return pluviometryDataTypes
            default:
                return []
        }
    }, [stationType, piezometryDataTypes, hydrometryDataTypes, pluviometryDataTypes])
}

const EventControlMeasuresCard = ({
    stationType,
    stationId,
    controls = [],
    onChange = () => {},
    eventDate,
    disabled = false,
}) => {
    const dataTypes = useModuleDataTypes(stationType)
    const [lastMeasures, setLastMeasures] = useState([])
    const [addedTypeIds, setAddedTypeIds] = useState([])
    const [addDataAnchor, setAddDataAnchor] = useState(null)

    useEffect(() => {
        if (hasValue(stationId)) {
            fetchStationLastMeasures(stationType, stationId)
                .then(measures => setLastMeasures(measures))
                .catch(() => setLastMeasures([]))
        }
    }, [stationType, stationId])

    const typeIds = useMemo(() => uniq([
        ...lastMeasures.map(m => m.dataType),
        ...controls.map(c => c.typeId),
        ...addedTypeIds,
    ].filter(id => hasValue(id))), [lastMeasures, controls, addedTypeIds])

    // Chaque ligne affichée est remontée au parent, pour que la validation à l'enregistrement la prenne en compte
    useEffect(() => {
        if (!disabled && typeIds.some(id => !controls.some(c => c.typeId === id))) {
            onChange(typeIds.map(id => controls.find(c => c.typeId === id) || { typeId: id }))
        }
    }, [typeIds, disabled])

    const otherDataTypes = dataTypes.filter(dt => !typeIds.includes(dt.id))

    // Contrôle affiché : date de l'événement tant que la date n'a pas été modifiée (une date vidée reste vide),
    // dernière valeur de l'historique tant qu'aucune n'est enregistrée
    const getControl = typeId => {
        const control = controls.find(c => c.typeId === typeId) || { typeId }
        const withDate = 'controlDate' in control ? control : { ...control, controlDate: eventDate }
        if (hasValue(withDate.lastValue)) {
            return withDate
        }
        const lastMeasure = lastMeasures.find(m => m.dataType === typeId)
        return { ...withDate, lastValue: lastMeasure?.value, lastValueDate: lastMeasure?.date }
    }

    const updateControl = (typeId, changes) => {
        const newControl = { ...getControl(typeId), ...changes }
        const difference = getControlDifference(newControl)
        const updated = !hasValue(difference) || difference === 0 ? { ...newControl, recalibration: false } : newControl
        // reconstruit dans l'ordre d'affichage : l'ordre des lignes dépend de celui des contrôles
        onChange(typeIds.map(id => (id === typeId ? updated : controls.find(c => c.typeId === id))).filter(c => !!c))
    }

    // Types de données présents sur la station (les autres ont été ajoutés via « Autre donnée »)
    const stationTypeIds = lastMeasures.map(m => m.dataType)

    const removeType = typeId => {
        setAddedTypeIds(prev => prev.filter(id => id !== typeId))
        onChange(controls.filter(c => c.typeId !== typeId))
    }

    return (
        <Card>
            <CardTitle title={i18n.controlMeasures} color={darkBlue} />
            <CardContent>
                <Grid container spacing={1} alignItems='center' padding={1}>
                    <Grid size={COLUMNS.label} />
                    <Grid size={COLUMNS.date}><Typography fontWeight='bold' noWrap>{`${i18n.controlDate} *`}</Typography></Grid>
                    <Grid size={COLUMNS.hour}><Typography fontWeight='bold' noWrap>{`${i18n.hour} *`}</Typography></Grid>
                    <Grid size={COLUMNS.value}><Typography fontWeight='bold' noWrap>{`${i18n.value} *`}</Typography></Grid>
                    <Grid size={COLUMNS.lastValue}><Typography fontWeight='bold' noWrap>{i18n.lastValue}</Typography></Grid>
                    <Grid size={COLUMNS.difference}><Typography fontWeight='bold' noWrap>{i18n.difference}</Typography></Grid>
                    <Grid size={COLUMNS.recalibration}><Typography fontWeight='bold' noWrap>{i18n.controlRecalibration}</Typography></Grid>
                    {typeIds.map(typeId => {
                        const control = getControl(typeId)
                        const dataType = dataTypes.find(dt => dt.id === typeId) || {}
                        const unit = dataType.unit ? ` ${dataType.unit}` : ''
                        const difference = getControlDifference(control)
                        const baseDate = control.controlDate ?? eventDate
                        return (
                            <React.Fragment key={typeId}>
                                <Grid size={COLUMNS.label} container alignItems='center' wrap='nowrap'>
                                    {!disabled && !stationTypeIds.includes(typeId) && (
                                        <Tooltip title={i18n.delete}>
                                            <IconButton size='small' sx={{ color: 'black', marginLeft: -1 }} onClick={() => removeType(typeId)}>
                                                <Close fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Typography>{`${dataType.label || typeId}${dataType.unit ? ` (${dataType.unit})` : ''}`}</Typography>
                                </Grid>
                                <Grid size={COLUMNS.date}>
                                    <Input
                                        key={`controlDate-${typeId}-${control.controlDate ?? ''}`}
                                        value={getDate(control.controlDate)}
                                        onChange={v => onChangeDate(v, d => updateControl(typeId, { controlDate: d }), {}, baseDate)}
                                        disabled={disabled}
                                    />
                                </Grid>
                                <Grid size={COLUMNS.hour}>
                                    <Input
                                        key={`controlHour-${typeId}-${control.controlDate ?? ''}`}
                                        value={hasValue(control.controlDate) ? getHour(control.controlDate) : ''}
                                        otherInputProps={{ onBlur: e => onChangeHour(formatHourInput(e.target.value), d => updateControl(typeId, { controlDate: d }), {}, safeBaseDate(baseDate)) }}
                                        disabled={disabled}
                                    />
                                </Grid>
                                <Grid size={COLUMNS.value}>
                                    <NumberField
                                        value={control.value}
                                        floatValue
                                        onChange={v => updateControl(typeId, { value: hasValue(v) && !isNaN(v) ? v : undefined })}
                                        disabled={disabled}
                                    />
                                </Grid>
                                <Grid size={COLUMNS.lastValue}>
                                    <Typography>
                                        {hasValue(control.lastValue) ? `${round(control.lastValue, 3)}${unit} - ${getFullDateMini(control.lastValueDate)}` : i18n.noData}
                                    </Typography>
                                </Grid>
                                <Grid size={COLUMNS.difference}>
                                    <Typography>{hasValue(difference) ? `${difference}${unit}` : ''}</Typography>
                                </Grid>
                                <Grid size={COLUMNS.recalibration}>
                                    {hasValue(difference) && difference !== 0 && (
                                        <Checkbox
                                            checked={!!control.recalibration}
                                            onChange={v => updateControl(typeId, { recalibration: v })}
                                            disabled={disabled}
                                        />
                                    )}
                                </Grid>
                            </React.Fragment>
                        )
                    })}
                    {!disabled && otherDataTypes.length > 0 && (
                        <Grid size={12} sx={{ paddingTop: 1, textAlign: 'center' }}>
                            <Button variant='contained' onClick={e => setAddDataAnchor(e.currentTarget)}>
                                {i18n.otherData}
                            </Button>
                            <Menu
                                anchorEl={addDataAnchor}
                                open={!!addDataAnchor}
                                onClose={() => setAddDataAnchor(null)}
                                slotProps={{ paper: { sx: { maxHeight: 300 } } }}
                            >
                                {otherDataTypes.map(dt => (
                                    <MenuItem
                                        key={dt.id}
                                        onClick={() => {
                                            setAddedTypeIds(prev => [...prev, dt.id])
                                            setAddDataAnchor(null)
                                        }}
                                    >
                                        {dt.unit ? `${dt.label} (${dt.unit})` : dt.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    )
}

EventControlMeasuresCard.propTypes = {
    stationType: PropTypes.oneOf([STATION_TYPE_NAME.piezometry, STATION_TYPE_NAME.hydrometry, STATION_TYPE_NAME.pluviometry]).isRequired,
    stationId: PropTypes.number,
    controls: PropTypes.arrayOf(PropTypes.shape({
        typeId: PropTypes.number,
        controlDate: PropTypes.number,
        value: PropTypes.number,
        lastValue: PropTypes.number,
        lastValueDate: PropTypes.number,
        recalibration: PropTypes.bool,
    })),
    onChange: PropTypes.func,
    eventDate: PropTypes.number,
    disabled: PropTypes.bool,
}

export default EventControlMeasuresCard
export { getControlsToSave, hasInvalidControls, getLevelControl, LEVEL_DATA_TYPE }
